import React, { useState } from "react";
import {
  Modal,
  Tabs,
  Form,
  Input,
  Button,
  Table,
  Upload,
  Space,
  message,
  Popconfirm,
  Empty,
  Typography,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  InboxOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import XLSX from "xlsx-js-style";

import {
  HEADERS_EXPORT_BEN,
  POST_INSERT_FIELD_COLLECTION,
  POST_LOAN_DB2,
} from "../../../API/apiUrls";

const { Dragger } = Upload;
const { Text } = Typography;

let rowKeySeq = 0;
const nextRowKey = () => `pending_${Date.now()}_${rowKeySeq++}`;

// ── แก้เลขสัญญาที่ excel ตัดเลข 0 นำหน้ารหัสบริษัท "022" ออกจนเหลือ "22" ──
const normalizeContno = (v) => {
  const trimmed = String(v ?? "").trim();
  return trimmed.startsWith("22") ? `0${trimmed}` : trimmed;
};

// ── แปลงแถว excel (header ภาษาไทย) → โครงสร้างสัญญา ──────────
const mapExcelRow = (row) => ({
  key: nextRowKey(),
  newContno: normalizeContno(row["เลขสัญญา"]),
});

const AddContractModal = ({ open, onCancel, onSuccess, lawyerOptions }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("manual");
  const [pendingRows, setPendingRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    form.resetFields();

    setPendingRows([]);
    onCancel?.(false);
  };

  // ── เพิ่มสัญญาทีละรายการ (ฟอร์ม) ───────────────────────────
  const handleAddManual = async (values) => {
    const newContno = (values.newContno || "").trim();
    if (pendingRows.some((r) => r.newContno === newContno)) {
      message.warning(`เลขสัญญา ${newContno} ถูกเพิ่มไว้แล้วในรายการ`);
      return;
    }

    const isRpsl = await loadLoanData(newContno);
    if (!isRpsl) {
      return message.error("ไม่สามารถเพิ่มได้ กรุณาเพิ่มแค่บัญชี P-LONE");
    }

    setPendingRows((prev) => [...prev, { key: nextRowKey(), newContno }]);

    form.resetFields();
    message.success(`เพิ่มสัญญา ${newContno} ลงในรายการแล้ว`);
  };

  // ── นำเข้าจากไฟล์ Excel ────────────────────────────────────
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(new Uint8Array(event.target.result), {
          type: "array",
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const sheetData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const mapped = sheetData.map(mapExcelRow).filter((r) => r.newContno);

        if (!mapped.length) {
          message.warning(
            'ไม่พบข้อมูลในไฟล์ (ต้องมีคอลัมน์ "เลขสัญญา" อย่างน้อย)',
          );
          return;
        }

        setPendingRows((prev) => {
          const existing = new Set(prev.map((r) => r.newContno));
          const duplicated = mapped.filter((r) => existing.has(r.newContno));
          const toAdd = mapped.filter((r) => !existing.has(r.newContno));

          if (duplicated.length) {
            message.warning(
              `ข้าม ${duplicated.length} รายการที่ซ้ำกับรายการเดิม`,
            );
          }
          if (toAdd.length) {
            message.success(`นำเข้าจาก Excel สำเร็จ ${toAdd.length} รายการ`);
          }
          return [...prev, ...toAdd];
        });
      } catch (error) {
        console.error("Error reading excel:", error);
        message.error("ไม่สามารถอ่านไฟล์ Excel ได้ กรุณาตรวจสอบรูปแบบไฟล์");
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // ป้องกัน antd Upload อัพโหลดไฟล์เอง
  };

  const handleRemoveRow = (key) => {
    setPendingRows((prev) => prev.filter((r) => r.key !== key));
  };

  const loadLoanData = async (contno) => {
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;
    try {
      const res = await axios.post(
        POST_LOAN_DB2,
        { CONTNO: `'${useContno}'` },
        { headers: HEADERS_EXPORT_BEN },
      );
      return res?.data?.[0]?.DATA_TYPE === "rpsl" || "rpsl-h";
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadLoanData:", error);
      }
      return false;
    }
  };

  // ── บันทึกรายการทั้งหมดไปยัง backend ──────────────────────
  const handleSaveAll = async () => {
    if (!pendingRows.length) {
      message.warning("ยังไม่มีสัญญาที่จะเพิ่ม");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        userId,
        contno: pendingRows.map((r) => r.newContno),
      };
      await axios.post(POST_INSERT_FIELD_COLLECTION, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      message.success(`เพิ่มสัญญาสำเร็จ ${pendingRows.length} รายการ`);
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error saving contracts:", error);
      message.error(
        error?.response?.data?.message ||
          "เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง",
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    { title: "เลขสัญญา", dataIndex: "newContno", key: "newContno", width: 130 },
    {
      title: "",
      key: "action",
      align: "center",
      width: 50,
      render: (_, record) => (
        <Popconfirm
          title="ลบรายการนี้ออกจากรายการที่จะเพิ่ม?"
          onConfirm={() => handleRemoveRow(record.key)}
          okText="ลบ"
          cancelText="ยกเลิก"
        >
          <Button danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const tabItems = [
    {
      key: "manual",
      label: "เพิ่มทีละสัญญา",
      children: (
        <Form form={form} layout="vertical" onFinish={handleAddManual}>
          <Form.Item
            label="เลขสัญญา"
            name="newContno"
            rules={[{ required: true, message: "กรุณากรอกเลขสัญญา" }]}
          >
            <Input placeholder="เช่น V2-0012430" />
          </Form.Item>

          <Button type="dashed" htmlType="submit" icon={<PlusOutlined />} block>
            เพิ่มลงในรายการ
          </Button>
        </Form>
      ),
    },
    {
      key: "excel",
      label: "นำเข้าจาก Excel",
      children: (
        <div>
          <Dragger
            accept=".xlsx,.xls"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            multiple={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              คลิกหรือลากไฟล์ Excel มาวางที่นี่เพื่อนำเข้าข้อมูล
            </p>
            <p className="ant-upload-hint" style={{ padding: "0 16px" }}>
              คอลัมน์ที่รองรับ: เลขสัญญา*
            </p>
          </Dragger>
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleFileUpload}
              showUploadList={false}
              multiple={false}
            >
              <Button icon={<UploadOutlined />} disabled>
                เลือกไฟล์
              </Button>
            </Upload>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="เพิ่มสัญญาเข้าชุดนอก"
      open={open}
      onCancel={handleClose}
      width={900}
      destroyOnHidden
      footer={
        <Space wrap>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!pendingRows.length}
            onClick={handleSaveAll}
          >
            บันทึกทั้งหมด{" "}
            {pendingRows.length > 0 ? `(${pendingRows.length} รายการ)` : ""}
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginBottom: 8 }}
      />

      <Text strong>รายการที่รอบันทึก ({pendingRows.length})</Text>
      {pendingRows.length > 0 ? (
        <Table
          style={{ marginTop: 8 }}
          size="small"
          columns={columns}
          dataSource={pendingRows}
          rowKey="key"
          pagination={{ pageSize: 5, hideOnSinglePage: true }}
          scroll={{ x: 850 }}
        />
      ) : (
        <Empty
          description="ยังไม่มีสัญญาที่เพิ่ม"
          style={{ margin: "16px 0" }}
        />
      )}
    </Modal>
  );
};

export default AddContractModal;
